import { useEffect, useRef, useState } from "react";
import { server } from "../../server";
import axios from "axios";
import Select from "react-select";
import { toast } from "react-toastify";
import { classList, sectionList, subjectList } from "../../utils";
import { getDecodedToken } from "../../auth";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { generateRandomNo } from "../../functions";

function HomeworkStatistics() {
  const [user, setUser] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [homework, setHomework] = useState([]);
  const [error, setError] = useState("");
  const MySwal = withReactContent(Swal);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const todayDate = new Date().toISOString().split("T")[0];
  const recordsPerPage = 10;
  const [filterType, setFilterType] = useState("All");
  const [filterClass, setFilterClass] = useState("All");
  const [filterSection, setFilterSection] = useState("All");
  const [filterSubject, setFilterSubject] = useState("All");
  const [filterTeacher, setFilterTeacher] = useState("All");

  const scrollContainerRef = useRef(null);

  const scrollLeft = () => {
    scrollContainerRef.current?.scrollBy({ left: -150, behavior: "smooth" });
  };

  const scrollRight = () => {
    scrollContainerRef.current?.scrollBy({ left: 150, behavior: "smooth" });
  };

  const uniqueValues = (key) => [
    "All",
    ...Array.from(new Set(homework.map((item) => item[key]).filter(Boolean))),
  ];

  const typeOptions = uniqueValues("type");
  const classOptions = uniqueValues("studentClass");
  const sectionOptions = uniqueValues("section");
  const subjectOptions = uniqueValues("subject");
  const teacherOptions = uniqueValues("staffFullName");

  useEffect(() => {
    const decodedData = getDecodedToken();
    if (decodedData) {
      setUser(decodedData);
    }
  }, []);

  const userId = user?.userId;
  const [formData, setFormData] = useState({
    userId: userId,
    studentClass: "",
    section: "",
    subject: "",
    file: null,
    description: "",
  });

  const totalFields = Object.keys(formData).length;
  const filledFields = Object.values(formData).filter(
    (val) => typeof val === "string" && val.trim() !== "",
  ).length;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({ ...prev, file: e.target.files[0] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let uploadedFileUrl = "";
    setLoading(true);

    const newHomeworkId = generateRandomNo();

    try {
      if (formData.file) {
        const presignRes = await axios.post(
          "https://7otfgyv0ke.execute-api.ap-south-1.amazonaws.com/prod/homework_presigned",
          {
            fileName: [formData.file.name],
          },
        );
        const uploadDetails =
          presignRes.data["body-json"].body.uploadDetails[0];
        await axios.put(uploadDetails.signedUrl, formData.file, {
          headers: {
            "Content-Type": uploadDetails.contentType,
          },
        });

        uploadedFileUrl = uploadDetails.key;
      }

      const payload = {
        userId: userId,
        homeworkId: newHomeworkId,
        studentClass: formData.studentClass,
        section: formData.section,
        subject: formData.subject,
        file: uploadedFileUrl,
        description: formData.description,
      };

      await axios.post(
        "https://7otfgyv0ke.execute-api.ap-south-1.amazonaws.com/prod/upload_homework_mongodb",
        payload,
        {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
          },
        },
      );
      toast.success("Homework Submitted", { position: "top-right" });
      const closeButton = document.querySelector('[data-bs-dismiss="modal"]');
      if (closeButton) {
        closeButton.click();
      }
      fetchHomeworkDetails();
      setFormData({
        userId: formData.userId,
        studentClass: "",
        section: "",
        subject: "",
        file: null,
        description: "",
      });
      document.getElementById("fileInput").value = "";
    } catch (error) {
      toast.error("Please Try Again", { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  const fetchHomeworkDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${server}/all_homework_of_currentDate_or_byDate`,
        {},
        {
          headers: { Authorization: `${localStorage.getItem("token")}` },
        },
      );
      const responseData = response.data?.["body-json"]?.body?.data;
      if (Array.isArray(responseData)) {
        const sortedData = responseData?.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
        );
        setHomework(sortedData);
      } else {
        console.error("Invalid data format from API", response.data);
      }
    } catch (error) {
      console.error("Error fetching fee details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHomeworkDetails();
  }, []);

  const filteredRecords = homework.filter((hw) => {
    const matchesSearch = Object.values(hw).some(
      (value) =>
        typeof value === "string" &&
        value.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    const matchesType = filterType === "All" || hw.type === filterType;
    const matchesClass =
      filterClass === "All" || hw.studentClass === filterClass;
    const matchesSection =
      filterSection === "All" || hw.section === filterSection;
    const matchesSubject =
      filterSubject === "All" || hw.subject === filterSubject;
    const matchesTeacher =
      filterTeacher === "All" || hw.staffFullName === filterTeacher;

    return (
      matchesSearch &&
      matchesType &&
      matchesClass &&
      matchesSection &&
      matchesSubject &&
      matchesTeacher
    );
  });

  const homeworkCountByTeacher = filteredRecords.reduce((acc, hw) => {
    const name = hw.staffFullName || "Unknown";
    acc[name] = (acc[name] || 0) + 1;
    return acc;
  }, {});

  const sortedHomeworkCountByTeacher = Object.entries(
    homeworkCountByTeacher,
  ).sort((a, b) => b[1] - a[1]);

  const homeworkCountMap = Object.fromEntries(sortedHomeworkCountByTeacher);

  // ✅ Reset pagination when searching
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // ✅ Apply pagination AFTER filtering
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord,
  );

  // Page Navigation
  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleDelete = async (homeworkIdToDelete) => {
    const result = await MySwal.fire({
      title: "Are you sure?",
      text: `Do you want to delete homework "${homeworkIdToDelete}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.post(
          `${server}/delete_homework_mongodb`,
          { homeworkId: homeworkIdToDelete },
          {
            headers: {
              Authorization: `${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (response.status === 200) {
          toast.success("Homework Deleted", { position: "top-right" });

          setHomework((prev) =>
            prev.filter((hw) => hw.homeworkId !== homeworkIdToDelete),
          );
        } else {
          toast.error("Deletion failed. Please try again.");
        }
      } catch (error) {
        toast.error("Error occurred while deleting. Try again.");
      }
    }
  };

  const handleExportAllHomeworkExcel = () => {
    if (!homework || homework.length === 0) {
      toast.error("No homework data to export");
      return;
    }

    const sheetData = homework.map((item) => ({
      "Homework ID": item.homeworkId || "",
      "Teacher ID": item.userId || "",
      Class: item.studentClass || "",
      Section: item.section || "",
      Subject: item.subject || "",
      Description: item.description || "",
      File: item.file ? item.file : "No",
      "Created Date": item.createdAt
        ? new Date(item.createdAt).toLocaleDateString("en-GB")
        : "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Homework");

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `AllHomework_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportTodaysHomeworkExcel = () => {
    if (!homework || homework.length === 0) {
      toast.error("No homework data to export");
      return;
    }

    const todayDate = new Date().toISOString().slice(0, 10);
    const todaysHomework = homework.filter((item) => {
      const itemDate = new Date(item.createdAt).toISOString().slice(0, 10);
      return itemDate === todayDate;
    });

    if (todaysHomework.length === 0) {
      toast.info("No homework submitted today");
      return;
    }

    const sheetData = todaysHomework.map((item) => ({
      "Homework ID": item.homeworkId || "",
      "Teacher ID": item.userId || "",
      Class: item.studentClass || "",
      Section: item.section || "",
      Subject: item.subject || "",
      Description: item.description || "",
      File: item.file ? item.file : "No",
      "Created Date": new Date(item.createdAt).toLocaleDateString("en-GB"),
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Today's Homework");

    XLSX.writeFile(workbook, `TodaysHomework_${todayDate}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportTeacherWiseHomeworkExcel = () => {
    if (!homework || homework.length === 0) {
      toast.error("No homework data to export");
      return;
    }

    const groupedByTeacher = homework.reduce((acc, item) => {
      const teacherId = item.userId || "Unknown_Teacher";
      if (!acc[teacherId]) acc[teacherId] = [];
      acc[teacherId].push(item);
      return acc;
    }, {});

    const workbook = XLSX.utils.book_new();

    Object.entries(groupedByTeacher).forEach(([teacherId, records]) => {
      const sheetData = records.map((item) => ({
        "Homework ID": item.homeworkId || "",
        Class: item.studentClass || "",
        Section: item.section || "",
        Subject: item.subject || "",
        Description: item.description || "",
        File: item.file ? item.file : "No",
        "Created Date": item.createdAt
          ? new Date(item.createdAt).toLocaleDateString("en-GB")
          : "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(sheetData);
      const sheetName = `${teacherId}`.substring(0, 31);
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `TeacherWise_Homework_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportClassWiseHomeworkExcel = () => {
    if (!homework || homework.length === 0) {
      toast.error("No homework data to export");
      return;
    }

    const workbook = XLSX.utils.book_new();
    const classGroups = {};

    homework.forEach((item) => {
      const cls = item.studentClass || "Unknown_Class";
      const sec = item.section || "Unknown_Section";

      if (!classGroups[cls]) classGroups[cls] = {};
      if (!classGroups[cls][sec]) classGroups[cls][sec] = [];

      classGroups[cls][sec].push(item);
    });

    Object.entries(classGroups).forEach(([className, sections]) => {
      const combinedData = [];

      const sortedSections = Object.keys(sections).sort();

      sortedSections.forEach((sec) => {
        const records = sections[sec];

        combinedData.push([`Section ${sec}`]);

        const formattedRecords = records.map((item) => ({
          "Homework ID": item.homeworkId || "",
          "Teacher ID": item.userId || "",
          Subject: item.subject || "",
          Description: item.description || "",
          File: item.file ? item.file : "No",
          "Created Date": item.createdAt
            ? new Date(item.createdAt).toLocaleDateString("en-GB")
            : "",
        }));

        const rows = XLSX.utils.sheet_to_json(
          XLSX.utils.json_to_sheet(formattedRecords),
          { header: 1, skipHeader: false },
        );

        combinedData.push(...rows);
        combinedData.push([]);
      });

      const worksheet = XLSX.utils.aoa_to_sheet(combinedData);
      const sheetName = `${className}`.substring(0, 31);
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `ClassWise_Homework_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportSubjectWiseHomeworkExcel = () => {
    if (!homework || homework.length === 0) {
      toast.error("No homework data to export");
      return;
    }

    const workbook = XLSX.utils.book_new();
    const subjectGroups = {};

    homework.forEach((item) => {
      const subject = item.subject || "Unknown_Subject";

      if (!subjectGroups[subject]) {
        subjectGroups[subject] = [];
      }

      subjectGroups[subject].push(item);
    });

    Object.entries(subjectGroups).forEach(([subjectName, records]) => {
      const sheetData = records.map((item) => ({
        "Homework ID": item.homeworkId || "",
        "Teacher ID": item.userId || "",
        Class: item.studentClass || "",
        Section: item.section || "",
        Description: item.description || "",
        File: item.file ? item.file : "No",
        "Created Date": item.createdAt
          ? new Date(item.createdAt).toLocaleDateString("en-GB")
          : "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(sheetData);
      const sheetName = subjectName.substring(0, 31);
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `SubjectWise_Homework_${today}.xlsx`);
    toast.success("Report Generated");
  };

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div
            class="modal fade gd-example-modal-lg"
            tabindex="-1"
            role="dialog"
            aria-labelledby="myLargeModalLabel"
            aria-hidden="true"
          >
            <div class="modal-dialog modal-lg">
              <div class="modal-content">
                <form onSubmit={handleSubmit}>
                  <div class="modal-header d-flex justify-content-between">
                    <h5 class="modal-title fs-4" id="exampleModalToggleLabel">
                      Add Homework{" "}
                      <span className="ms-3 text-secondary">
                        ({String(filledFields).padStart(2, "0")}/
                        {String(totalFields).padStart(2, "0")})
                      </span>
                    </h5>
                    <div className="d-flex align-items-center">
                      <span class="badge bg-success-soft me-3 px-3">
                        Editable
                      </span>
                      <div class="form-check form-switch me-4">
                        <input
                          type="checkbox"
                          class="form-check-input m-0"
                          checked
                        />
                      </div>
                      <button
                        type="button"
                        class="btn-close"
                        data-bs-dismiss="modal"
                        aria-label="Close"
                      ></button>
                    </div>
                  </div>

                  <div class="modal-body">
                    <div className="row g-2 mb-2">
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1">
                          Class <span className="text-danger">*</span>
                          {"  "}&nbsp;
                        </label>
                        <Select
                          name="studentClass"
                          value={
                            classList
                              .map((cls) => ({ label: cls, value: cls }))
                              .find(
                                (opt) => opt.value === formData.studentClass,
                              ) || null
                          }
                          onChange={(selected) =>
                            handleChange({
                              target: {
                                name: "studentClass",
                                value: selected?.value || "",
                              },
                            })
                          }
                          options={classList.map((cls) => ({
                            label: cls,
                            value: cls,
                          }))}
                          placeholder="Select Class"
                        />
                      </div>
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1">
                          Section <span className="text-danger">*</span>
                          {"  "}&nbsp;
                        </label>
                        <Select
                          name="section"
                          value={
                            sectionList
                              .map((section) => ({
                                label: section,
                                value: section,
                              }))
                              .find((opt) => opt.value === formData.section) ||
                            null
                          }
                          onChange={(selected) =>
                            handleChange({
                              target: {
                                name: "section",
                                value: selected?.value || "",
                              },
                            })
                          }
                          options={sectionList.map((section) => ({
                            label: section,
                            value: section,
                          }))}
                          placeholder="Select Section"
                        />
                      </div>
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1">
                          Subject <span className="text-danger">*</span>
                          {"  "}&nbsp;
                        </label>
                        <Select
                          name="subject"
                          value={
                            subjectList
                              .map((subject) => ({
                                label: subject,
                                value: subject,
                              }))
                              .find((opt) => opt.value === formData.subject) ||
                            null
                          }
                          onChange={(selected) =>
                            handleChange({
                              target: {
                                name: "subject",
                                value: selected?.value || "",
                              },
                            })
                          }
                          options={subjectList.map((subject) => ({
                            label: subject,
                            value: subject,
                          }))}
                          placeholder="Select Subject"
                        />
                      </div>
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1">
                          Upload File
                          {"  "}&nbsp;
                        </label>
                        <input
                          type="file"
                          id="fileInput"
                          className="form-control"
                          onChange={handleFileChange}
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label mb-1">
                          Description <span className="text-danger">*</span>
                          {"  "}&nbsp;
                        </label>
                        <textarea
                          type="text"
                          rows={3}
                          className="form-control"
                          placeholder="Enter Description"
                          name="description"
                          value={formData.description}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div class="modal-footer d-flex justify-content-between py-2">
                    <div>
                      <button
                        type="button"
                        class="btn btn-secondary px-3 me-1 rounded-pill"
                        data-bs-dismiss="modal"
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>
                      <button
                        type="submit"
                        class="btn btn-primary px-5 rounded-pill fw-normal"
                        disabled={loading}
                      >
                        {loading ? (
                          <div
                            class="spinner-border spinner-border-sm"
                            role="status"
                          ></div>
                        ) : (
                          "Submit"
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div class="container-fluid my-3 px-3">
            <div className="row g-2">
              <div className="col-lg-8">
                <div class="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                  <table class="table mb-0 text-nowrap table-centered">
                    <thead class="table-light">
                      <tr>
                        <th className="w-10">No.</th>
                        <th className="lh-1 w-60">
                          Details
                          <i className="bi bi-info-circle ms-2 text-dark"></i>
                          <br />
                          <span className="fs-8 fw-normal text-secondary">
                            Displaying Information as per Record
                          </span>
                        </th>
                        <th className="lh-1 w-30">
                          Action
                          <i className="bi bi-person-gear ms-2 text-dark"></i>
                          <br />
                          <span className="fs-8 fw-normal text-secondary">
                            Role-Specific Action Panel
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentRecords.map((homework, index) => (
                        <tr>
                          <td>{indexOfFirstRecord + index + 1}.</td>
                          <td className="lh-sm py-4 text-wrap">
                            <span class="text-dark">Homework ID</span> &mdash;{" "}
                            {homework.homeworkId}
                            <br />
                            <span class="text-dark">
                              Teacher ID
                            </span> &mdash; {homework.userId}
                            <br />
                            <span class="text-dark">Teacher's Name</span>{" "}
                            &mdash; {homework.staffFullName}
                            <br />
                            <span class="text-dark">Class</span> &mdash;{" "}
                            {homework.studentClass} <br />
                            <span class="text-dark">Section</span> &mdash;{" "}
                            {homework.section} <br />
                            <span class="text-dark">Subject</span> &mdash;{" "}
                            {homework.subject} <br />
                            <span class="text-dark">
                              Description
                            </span> &mdash;{" "}
                            {homework.description
                              .split(" ")
                              .reduce((acc, word, index) => {
                                const last = acc.length - 1;
                                if (index % 5 === 0) {
                                  acc.push([word]);
                                } else {
                                  acc[last].push(word);
                                }
                                return acc;
                              }, [])
                              .map((group, i) => (
                                <span key={i}>
                                  {group.join(" ")}
                                  <br />
                                </span>
                              ))}
                            <br />
                            {homework.file && (
                              <>
                                <span className="text-dark">File</span> &mdash;{" "}
                                <a
                                  href={homework.file}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Click Here
                                </a>
                                <br />
                              </>
                            )}
                            <span className="text-dark">Date / Time</span>{" "}
                            &mdash;{" "}
                            <span className="text-uppercase">
                              {new Date(homework.createdAt).toLocaleString(
                                "en-GB",
                                {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                },
                              )}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex flex-column gap-1 width-fit-content">
                              <span class="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm">
                                Update
                              </span>
                              <span
                                onClick={() =>
                                  handleDelete(homework.homeworkId)
                                }
                                class="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                              >
                                Delete
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="col-lg-4">
                <div class="">
                  <div className="mb-3">
                    <div className="d-flex bg-white justify-content-between rounded-4">
                      <form
                        className="d-flex align-items-center"
                        onSubmit={(e) => e.preventDefault()}
                      >
                        <span className="position-absolute ps-3 search-icon">
                          <i className="bi bi-filter-circle-fill ms-1" />
                        </span>
                        <input
                          type="search"
                          className="form-control ps-7 rounded-3 border-0 rounded-4"
                          placeholder="Filter Data"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </form>
                      <Link
                        data-bs-toggle="modal"
                        data-bs-target=".gd-example-modal-lg"
                        class="btn btn-warning px-5 fs-6 fw-medium rounded-4 fit-content"
                      >
                        Add Homework
                      </Link>
                    </div>
                    <div className="mt-2 ms-4">
                      <nav aria-label="breadcrumb">
                        <ol class="breadcrumb pe-3">
                          <li class="breadcrumb-item">
                            <a href="#">Homework</a>
                          </li>
                          <li
                            class="breadcrumb-item active"
                            aria-current="page"
                          >
                            Add Homework
                          </li>
                        </ol>
                      </nav>
                    </div>
                  </div>

                  <div className="p-4 bg-white rounded-4">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        {loading ? (
                          <div
                            className="spinner-border text-dark data-loader-spinner"
                            role="status"
                          ></div>
                        ) : (
                          <img src="/assets/images/folder.svg" />
                        )}

                        <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                          Homework ({filteredRecords.length})
                          <br />
                          <span className="text-secondary fs-6 fw-medium lh-sm">
                            2.4 MB Size
                          </span>
                        </p>
                      </div>
                      <span className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer">
                        Export
                      </span>
                    </div>

                    <div class="px-4 py-2 mt-3 bg-light rounded-4">
                      <li className="list-group-item px-0">
                        <a
                          className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                          data-bs-toggle="collapse"
                          href="#specifications"
                          role="button"
                          aria-expanded="false"
                          aria-controls="specifications"
                        >
                          <p className="m-0 fw-medium lh-sm text-dark">
                            Download Report
                          </p>
                          <span className="chevron-arrow ms-4">
                            <i className="bi bi-chevron-down fs-4" />
                          </span>
                        </a>
                        <div
                          className="collapse"
                          id="specifications"
                          data-bs-parent="#ecommerceAccordion"
                        >
                          <div className="py-3">
                            <div class="rounded-4 d-flex flex-wrap">
                              <span
                                onClick={handleExportAllHomeworkExcel}
                                class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                              >
                                All Homework
                              </span>
                              <span
                                onClick={handleExportTodaysHomeworkExcel}
                                class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                              >
                                Today's Homework
                              </span>
                              <span
                                onClick={handleExportTeacherWiseHomeworkExcel}
                                class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                              >
                                Teacher Wise Homework
                              </span>
                              <span
                                onClick={handleExportClassWiseHomeworkExcel}
                                class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                              >
                                Class/Section Wise Homework
                              </span>
                              <span
                                onClick={handleExportSubjectWiseHomeworkExcel}
                                class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                              >
                                Subject Wise Homework
                              </span>
                            </div>
                          </div>
                        </div>
                      </li>
                    </div>

                    <div>
                      <div className="position-relative mt-3">
                        <span
                          className="circle-slider-button bg-dark rounded-circle position-absolute start-0 top-50 translate-middle-y z-1 cursor-pointer"
                          onClick={scrollLeft}
                        >
                          <i className="bi bi-chevron-left text-white"></i>
                        </span>
                        <div
                          className="gap-1 d-flex overflow-x-auto px-6"
                          ref={scrollContainerRef}
                          style={{
                            scrollBehavior: "smooth",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {typeOptions.map((opt) => (
                            <span
                              key={opt}
                              className={`btn btn-tag btn-sm ${
                                filterType === opt
                                  ? "bg-success-subtle border border-success text-success-emphasis"
                                  : ""
                              }`}
                              onClick={() => setFilterType(opt)}
                            >
                              {opt}
                            </span>
                          ))}
                        </div>
                        <span
                          className="circle-slider-button bg-dark rounded-circle position-absolute end-0 top-50 translate-middle-y z-1 cursor-pointer"
                          onClick={scrollRight}
                        >
                          <i className="bi bi-chevron-right text-white"></i>
                        </span>
                      </div>

                      <div className="position-relative mt-2">
                        <span
                          className="circle-slider-button bg-dark rounded-circle position-absolute start-0 top-50 translate-middle-y z-1 cursor-pointer"
                          onClick={() =>
                            document
                              .getElementById("classScroll")
                              .scrollBy({ left: -120, behavior: "smooth" })
                          }
                        >
                          <i className="bi bi-chevron-left text-white"></i>
                        </span>
                        <div
                          id="classScroll"
                          className="gap-1 d-flex overflow-x-auto px-6"
                          style={{
                            scrollBehavior: "smooth",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {classOptions.map((opt) => (
                            <span
                              key={opt}
                              className={`btn btn-tag btn-sm ${
                                filterClass === opt
                                  ? "bg-success-subtle border border-success text-success-emphasis"
                                  : ""
                              }`}
                              onClick={() => setFilterClass(opt)}
                            >
                              {opt}
                            </span>
                          ))}
                        </div>
                        <span
                          className="circle-slider-button bg-dark rounded-circle position-absolute end-0 top-50 translate-middle-y z-1 cursor-pointer"
                          onClick={() =>
                            document
                              .getElementById("classScroll")
                              .scrollBy({ left: 120, behavior: "smooth" })
                          }
                        >
                          <i className="bi bi-chevron-right text-white"></i>
                        </span>
                      </div>

                      <div className="position-relative mt-2">
                        <span
                          className="circle-slider-button bg-dark rounded-circle position-absolute start-0 top-50 translate-middle-y z-1 cursor-pointer"
                          onClick={() =>
                            document
                              .getElementById("sectionScroll")
                              .scrollBy({ left: -120, behavior: "smooth" })
                          }
                        >
                          <i className="bi bi-chevron-left text-white"></i>
                        </span>
                        <div
                          id="sectionScroll"
                          className="gap-1 d-flex overflow-x-auto px-6"
                          style={{
                            scrollBehavior: "smooth",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {sectionOptions.map((opt) => (
                            <span
                              key={opt}
                              className={`btn btn-tag btn-sm ${
                                filterSection === opt
                                  ? "bg-success-subtle border border-success text-success-emphasis"
                                  : ""
                              }`}
                              onClick={() => setFilterSection(opt)}
                            >
                              {opt}
                            </span>
                          ))}
                        </div>
                        <span
                          className="circle-slider-button bg-dark rounded-circle position-absolute end-0 top-50 translate-middle-y z-1 cursor-pointer"
                          onClick={() =>
                            document
                              .getElementById("sectionScroll")
                              .scrollBy({ left: 120, behavior: "smooth" })
                          }
                        >
                          <i className="bi bi-chevron-right text-white"></i>
                        </span>
                      </div>

                      <div className="position-relative mt-2">
                        <span
                          className="circle-slider-button bg-dark rounded-circle position-absolute start-0 top-50 translate-middle-y z-1 cursor-pointer"
                          onClick={() =>
                            document
                              .getElementById("subjectScroll")
                              .scrollBy({ left: -120, behavior: "smooth" })
                          }
                        >
                          <i className="bi bi-chevron-left text-white"></i>
                        </span>
                        <div
                          id="subjectScroll"
                          className="gap-1 d-flex overflow-x-auto px-6"
                          style={{
                            scrollBehavior: "smooth",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {subjectOptions.map((opt) => (
                            <span
                              key={opt}
                              className={`btn btn-tag btn-sm ${
                                filterSubject === opt
                                  ? "bg-success-subtle border border-success text-success-emphasis"
                                  : ""
                              }`}
                              onClick={() => setFilterSubject(opt)}
                            >
                              {opt}
                            </span>
                          ))}
                        </div>
                        <span
                          className="circle-slider-button bg-dark rounded-circle position-absolute end-0 top-50 translate-middle-y z-1 cursor-pointer"
                          onClick={() =>
                            document
                              .getElementById("subjectScroll")
                              .scrollBy({ left: 120, behavior: "smooth" })
                          }
                        >
                          <i className="bi bi-chevron-right text-white"></i>
                        </span>
                      </div>

                      <div className="position-relative my-3">
                        <span
                          className="circle-slider-button bg-dark rounded-circle position-absolute start-0 top-50 translate-middle-y z-1 cursor-pointer"
                          onClick={() =>
                            document
                              .getElementById("teacherScroll")
                              .scrollBy({ left: -120, behavior: "smooth" })
                          }
                        >
                          <i className="bi bi-chevron-left text-white"></i>
                        </span>

                        <div
                          id="teacherScroll"
                          className="gap-1 d-flex overflow-x-auto px-6"
                          style={{
                            scrollBehavior: "smooth",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {teacherOptions.map((opt) => {
                            const count = homeworkCountMap[opt] || 0;

                            return (
                              <span
                                key={opt}
                                className={`btn btn-tag btn-sm ${
                                  filterTeacher === opt
                                    ? "bg-success-subtle border border-success text-success-emphasis"
                                    : ""
                                }`}
                                onClick={() => setFilterTeacher(opt)}
                              >
                                {opt} {count > 0 && `(${count})`}
                              </span>
                            );
                          })}
                        </div>

                        <span
                          className="circle-slider-button bg-dark rounded-circle position-absolute end-0 top-50 translate-middle-y z-1 cursor-pointer"
                          onClick={() =>
                            document
                              .getElementById("teacherScroll")
                              .scrollBy({ left: 120, behavior: "smooth" })
                          }
                        >
                          <i className="bi bi-chevron-right text-white"></i>
                        </span>
                      </div>

                      <button
                        className="btn btn-info px-5 fs-6 fw-semibold rounded-pill fit-content"
                        onClick={() => {
                          setFilterType("All");
                          setFilterClass("All");
                          setFilterSection("All");
                          setFilterSubject("All");
                          setFilterTeacher("All");
                        }}
                      >
                        Reset Filters
                      </button>
                    </div>
                  </div>

                  <div className="my-4">
                    <nav>
                      <ul className="pagination justify-content-center mb-0">
                        <li
                          className={`page-item ${
                            currentPage === 1 ? "disabled" : ""
                          }`}
                        >
                          <button className="page-link" onClick={prevPage}>
                            <i className="bi bi-chevron-left"></i>
                          </button>
                        </li>

                        {(() => {
                          const pageNumbers = [];
                          pageNumbers.push(1);

                          if (currentPage > 3) {
                            pageNumbers.push("left-ellipsis");
                          }

                          if (currentPage !== 1 && currentPage !== totalPages) {
                            pageNumbers.push(currentPage);
                          }

                          if (currentPage < totalPages - 2) {
                            pageNumbers.push("right-ellipsis");
                          }

                          if (totalPages > 1) {
                            pageNumbers.push(totalPages);
                          }

                          return pageNumbers.map((page, index) => {
                            if (
                              page === "left-ellipsis" ||
                              page === "right-ellipsis"
                            ) {
                              return (
                                <li key={index} className="page-item disabled">
                                  <span className="page-link">...</span>
                                </li>
                              );
                            }

                            return (
                              <li
                                key={index}
                                className={`page-item ${
                                  currentPage === page ? "active" : ""
                                }`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() => setCurrentPage(page)}
                                >
                                  {page}
                                </button>
                              </li>
                            );
                          });
                        })()}

                        <li
                          className={`page-item ${
                            currentPage === totalPages ? "disabled" : ""
                          }`}
                        >
                          <button className="page-link" onClick={nextPage}>
                            <i className="bi bi-chevron-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default HomeworkStatistics;
